/**
 * A reusable animated container component that leverages `framer-motion` to provide
 * various animation effects such as fade, slide, scale, or custom animations.
 *
 * @component
 * @param {AnimatedContainerProps} props - The props for the AnimatedContainer component.
 * @param {boolean} props.isVisible - Determines whether the container is visible or not.
 * @param {React.ReactNode} props.children - The content to be rendered inside the animated container.
 * @param {'fade' | 'slide' | 'scale' | 'custom'} [props.animationType='default'] - The type of animation to apply.
 * @param {'left' | 'right' | 'up' | 'down'} [props.slideDirection='left'] - The direction of the slide animation (only applicable if `animationType` is 'slide').
 * @param {MotionProps} [props.customAnimationProps] - Custom animation properties for the container (only applicable if `animationType` is 'custom').
 * @param {string} [props.className] - Additional CSS class names to apply to the container.
 * @param {React.CSSProperties} [props.style] - Inline styles to apply to the container.
 * @param {MotionProps} [props.motionProps] - Additional motion properties to pass to the `motion.div` element.
 *
 * @returns {React.FC<AnimatedContainerProps>} A motion-enabled container with the specified animation.
 *
 * @example
 * ```tsx
 * <AnimatedContainer
 *   isVisible={true}
 *   animationType="fade"
 *   className="my-container"
 *   style={{ backgroundColor: 'lightblue' }}
 * >
 *   <p>This is an animated container!</p>
 * </AnimatedContainer>
 * ```
 */
import { AnimatePresence, motion, type MotionProps } from 'framer-motion'
import React from 'react'

interface AnimatedContainerProps extends MotionProps {
  isVisible: boolean
  children: React.ReactNode
  animationType?: 'fade' | 'slide' | 'scale' | 'custom'
  slideDirection?: 'left' | 'right' | 'up' | 'down'
  customAnimationProps?: MotionProps
  className?: string
  style?: React.CSSProperties
}

const getSlideAnimation = (direction: 'left' | 'right' | 'up' | 'down'): MotionProps => {
  const distance = 50
  switch (direction) {
    case 'left':
      return { initial: { opacity: 0, x: -distance }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -distance } }
    case 'right':
      return { initial: { opacity: 0, x: distance }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: distance } }
    case 'up':
      return { initial: { opacity: 0, y: -distance }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -distance } }
    case 'down':
      return { initial: { opacity: 0, y: distance }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: distance } }
    default:
      return { initial: {}, animate: {}, exit: {} }
  }
}

const animationPresets = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  },
  scale: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
    transition: { duration: 0.3 }
  },
  default: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.3 }
  }
} as const

const AnimatedContainer: React.FC<AnimatedContainerProps> = ({
  isVisible,
  children,
  animationType = 'default',
  slideDirection = 'left',
  customAnimationProps,
  className,
  style,
  ...motionProps
}) => {
  const selectedAnimation =
    animationType === 'slide'
      ? getSlideAnimation(slideDirection)
      : animationPresets[animationType as keyof typeof animationPresets] || animationPresets.default

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          {...(animationType === 'custom' ? customAnimationProps : selectedAnimation)}
          style={{ overflow: 'hidden', ...style }}
          className={className}
          {...motionProps}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default AnimatedContainer
